/**
 * Inhalte der Landingpage „Fachkraft für Musik und Sprache im Elementarbereich"
 * (`/ausbildung/fachkraft-musik-und-sprache`).
 *
 * Quelle: Prototyp `17_PROTOTYP_Landingpage_Musik-und-Sprache.html` und Auftrag
 * `17_AUFTRAG_Claude-Code_Landingpage-Musik-und-Sprache.md` im Drive-Ordner
 * MusiKonzept_Knowledge/Produkt/Ausbildung-Sprachfoerderung (Stand 28.09.2026).
 * Die Texte sind wörtlich übernommen. Wer sie ändern will, ändert sie hier und
 * nicht in der Seite.
 *
 * Bewusst reine Daten ohne React: Die beiden interaktiven Blöcke lesen nur
 * hieraus, es gibt keine Datenbankabfrage (Auftrag, Abschnitt 3).
 *
 * Nicht auf diese Seite gehören (Auftrag, Abschnitte 8 und 9):
 *   - „anerkannt", „zugelassen", „staatlich geprüft": Der ZFU-Status ist offen.
 *   (Seit 03.10.2026 geklärt: Alle vier Kurse sind Pflicht. „vier Pflichtkurse“
 *   darf jetzt vorkommen, muss aber nicht.)
 *   - die gestrichene zweite Zertifikatsvariante.
 *   Ein Test in `src/config/__tests__/musikUndSpracheLandingpage.test.ts`
 *   nagelt das fest.
 */

/**
 * Wo die Bilder liegen. Alles unter `public/`, damit sich jedes Bild durch
 * einen Dateitausch mit gleichem Namen ersetzen lässt, ohne Code zu ändern.
 */
const BILDER = "/images/musik-und-sprache";

/**
 * Hero-Bild. Foto: Erzieherin mit drei Kindern beim gemeinsamen Musizieren.
 * Wird durch Dateitausch unter demselben Namen ersetzt (`hero.webp`), dazu
 * entsteht `og.jpg` (Vorschaubild beim Teilen) aus demselben Bild.
 */
export const HERO_BILD = {
  src: `${BILDER}/hero.webp`,
  alt: "Erzieherin musiziert mit drei Kindern: Trommel, Rassel und Sprechblasen mit Noten und ABC",
  breite: 1200,
  hoehe: 675,
} as const;

export interface Zahl {
  wert: string;
  text: string;
}

export const ZAHLEN: readonly Zahl[] = [
  { wert: "4", text: "Kurse im Lernbereich" },
  { wert: "8", text: "Bausteine je Altersgruppe" },
  { wert: "140", text: "Lektionen U3 und Ü3" },
  { wert: "~50", text: "Unterrichtsstunden" },
  { wert: "∞", text: "Zugang ohne Ablaufdatum" },
];

export interface Kurs {
  nummer: number;
  /** Slug in `public.courses`, aus dem das Bild stammt. */
  slug: string;
  titel: string;
  text: string;
  dozentin: string;
  /**
   * Wörtlich `courses.thumbnail_url` des Slugs, gelesen am 28.09.2026.
   *
   * Nicht zur Laufzeit abgefragt: Kurs 1 und 4 stehen auf
   * `is_published = false`, und die Policy `courses_select` zeigt Besucher:innen
   * ohne Adminrolle nur veröffentlichte Kurse. Eine Abfrage hätte für die
   * Hälfte der Kacheln kein Bild geliefert. Ändert sich ein Kursbild in der
   * Datenbank, hier nachziehen.
   */
  bild: string;
  bildAlt: string;
}

export const KURSE: readonly Kurs[] = [
  {
    nummer: 1,
    slug: "grundlagen-sprachentwicklung-verstehen",
    titel: "Sprachentwicklung im frühen Kindesalter",
    text: "Wie Sprache bei Kindern entsteht, welche Phasen sie durchläuft und was das für deine Praxis bedeutet.",
    dozentin: "Tabitha Thieme",
    bild: "https://vqcngohkfhvdrmnysyck.supabase.co/storage/v1/object/public/termin-bilder/thumbnails/course-7a82f3fc-e67e-4e4a-acab-6792e475381c-tabitha.jpg?v=1782378833321",
    bildAlt: "Kurs Grundlagen der Sprachentwicklung",
  },
  {
    nummer: 2,
    slug: "musikalische-sprachfoerderung-u3",
    titel: "Musikalische Sprachförderung U3",
    text: "Sprache durch Musik bei den Jüngsten: Verse, Lieder und Spielideen für Kinder unter drei Jahren.",
    dozentin: "Kati Breuer",
    bild: "https://vqcngohkfhvdrmnysyck.supabase.co/storage/v1/object/public/termin-bilder/thumbnails/course-2e545ca8-5d4c-40b2-ab8a-10e742e82ba8-kati.jpg?v=1782378833748",
    bildAlt: "Kurs Musikalische Sprachförderung U3",
  },
  {
    nummer: 3,
    slug: "musikalische-sprachfoerderung-ue3",
    titel: "Musikalische Sprachförderung Ü3",
    text: "Wirkungsvolle musikalische Sprachförderung für Kinder über drei Jahren: konkret, praxisnah, alltagstauglich.",
    dozentin: "Nicole Knorr",
    bild: "https://vqcngohkfhvdrmnysyck.supabase.co/storage/v1/object/public/termin-bilder/thumbnails/course-2d5b54cd-d333-4254-bdfe-874e46ff137d-nicole.jpg?v=1782378834168",
    bildAlt: "Kurs Musikalische Sprachförderung Ü3",
  },
  {
    nummer: 4,
    slug: "sprachlich-durch-das-jahr",
    titel: "Sprachlich durch das Jahr",
    text: "Sprachförderung mit Musik entlang der vier Jahreszeiten: Lieder, Verse und Spielideen für den Kita-Alltag.",
    dozentin: "Kati Breuer & Nicole Knorr",
    // Liegt als einziges Kursbild nicht im Supabase-Storage, sondern als
    // Lovable-Asset unter www.musikonzept.de. So steht es in der Datenbank.
    bild: "https://www.musikonzept.de/__l5e/assets-v1/814db46f-81ab-43b5-845f-88519d45ab6a/sprachlich-durch-die-jahreszeiten.jpg",
    bildAlt: "Kurs Sprachlich durch das Jahr",
  },
];

export interface Baustein {
  name: string;
  /**
   * Nummer der Aquarell-Datei (`bausteine/<icon>.webp`). Die Reihenfolge der
   * Bausteine auf der Seite ist eine andere als die der Dateien.
   */
  icon: number;
  beschreibung: string;
  /** Lektionstitel Kati Breuer, Krippe · U3. */
  u3: readonly string[];
  /** Lektionstitel Nicole Knorr, Kita · Ü3. */
  ue3: readonly string[];
}

/**
 * Array `B` aus dem Prototyp, 1:1.
 *
 * Die Lektionstitel sind am 14.10.2026 wörtlich aus den Kursen übernommen
 * (`course_lessons`, ohne Einführung und Reflexion): je Baustein sechs Lieder
 * und Spielideen bei Kati (U3) und bei Nicole (Ü3). Vorher waren die Ü3-Titel
 * aus Dateinamen rekonstruiert, und bei Nicole fehlten in fünf Bausteinen
 * Inhalte (Hinweis von Nicole). Wer in einem Kurs eine Lektion umbenennt,
 * zieht sie hier nach; der Test daneben verlangt sechs je Spalte.
 */
export const BAUSTEINE: readonly Baustein[] = [
  {
    name: "Hören und Sprachmelodie",
    icon: 1,
    beschreibung:
      "Zuhören ist der Anfang von allem. Wie Kinder Klänge unterscheiden, Sprachmelodie aufnehmen und Reime entdecken.",
    u3: ["Hallo, ihr lieben Leute", "So leise klingt das Meer", "Geräusche erkennen", "Reimpaare hören", "Die Schnecke und die Rosenhecke", "Wo ist Amirs Teddy?"],
    ue3: ["Das tanzende Krokodil", "Komm, sing mir nach", "Reimemonster", "Die Stimme geht auf Reise", "Hört mal, wer da klopft?", "Der/die Maler(in) und die Farben"],
  },
  {
    name: "Rhythmus und Wiederholung",
    icon: 2,
    beschreibung:
      "Sprache hat einen Puls. Silben klatschen, Verse wiederholen: Der Satz sitzt, bevor das Kind ihn erklären kann.",
    u3: ["Ich sehe was, was du nicht siehst", "Was mein Tuch alles kann", "Wachgerüttelt", "Räder drehen", "Der Regentag", "Jetzt ist Instrumentenzeit"],
    ue3: ["Der Haufen-Rap", "Ri-Ra-Räuber", "Becher-Spruch", "Ein Krokodil", "Zwei Elefanten", "Wie die Dinos"],
  },
  {
    name: "Stimme und Betonung",
    icon: 4,
    beschreibung:
      "Laut und leise, hoch und tief, schnell und langsam. Wie Kinder ihre eigene Stimme ausprobieren und dabei Laute schärfen.",
    u3: ["Im Land der Zwerge", "All meine Gefühle haben ihren Platz", "Der Ausflug", "Der Stimmenflieger", "Langsam und schnell", "Laut und leise"],
    ue3: ["Besuch auf Burg Gruselstein", "Fünf kleine Bienen", "Die Tiere sagen „Hallo“", "Dschungel-Safari", "Die Stimm-Rennbahn", "Tuchreise"],
  },
  {
    name: "Bewegung und Sprache",
    icon: 3,
    beschreibung:
      "Kinder begreifen Wörter mit dem ganzen Körper. Bewegungslieder, bei denen jedes Wort eine Geste bekommt.",
    u3: ["Das ist mein Gesicht", "Nach dem Flitzen leise sitzen", "Auf der Baustelle", "Rund und fein", "Wie wollen wir als Nächstes geh’n", "Mein Körper macht mit"],
    ue3: ["Der Franz, der tanzt", "In einem Tickitackitucki-Häuschen", "Eine kleine, grüne Schlange", "Die kleine Lokomotive", "Sprechvers für Tücher oder Küchenschwämme", "Die Trommel spricht, dein Körper hört"],
  },
  {
    name: "Abgrenzung zur Therapie",
    icon: 7,
    beschreibung:
      "Wo deine Arbeit aufhört und Logopädie anfängt. Woran du einen Förderbedarf erkennst und wie du ihn ansprichst.",
    u3: ["Rubbeldikatz, du bist mein Schatz", "Drei Schimpansen mit ’ner Ananas", "Mit dem Bus durch die Stadt", "Was ist denn hier los?", "Kritzelkratzel", "Fünf Gespenster vor dem Fenster"],
    ue3: ["Die merkwürdigen Eulen", "Linke Hand und rechte Hand", "Atemspiele", "Ein tierischer Gruß", "Der Piratenschatz", "Reise durch das Geisterschloss"],
  },
  {
    name: "Sprachbildung im Alltag",
    icon: 6,
    beschreibung:
      "Keine Extra-Stunde. Sprache beim Anziehen, beim Essen, beim Aufräumen, in den Momenten, die ohnehin jeden Tag kommen.",
    u3: ["Obstsalat-Rap", "Bei uns in der Krippe", "Auf dem Spielplatz", "Suppe kochen", "Mila geht raus", "Wir räumen auf"],
    ue3: ["Guten Morgen, lieber Körper", "Ich war’s nicht", "Die durstigen Finger", "Rhythmisches Reim-Spiel", "Ab in die Kita", "Rhythmischer Einkauf am Wochenmarkt"],
  },
  {
    name: "Bewusste sprachliche Impulse",
    icon: 5,
    beschreibung:
      "Der gezielte Anstoß. Wie du mit einer Frage, einer Pause oder einem Quatschwort das Kind ins Sprechen bringst.",
    u3: ["Pico, unser Papagei", "Fritz und Frieda und Fine und Franz", "Die Hände machen Quatsch", "Groß und klein und weich und hart", "Eine Feder fliegt im Wind", "Die kleine Raupe"],
    ue3: ["Wir singen Schubidua", "Larifari", "Ene, mene Lippen", "Frösche-Spruch", "Was die Zunge alles kann", "Klitsch Klatsch"],
  },
  {
    name: "Emotionale Sicherheit",
    icon: 8,
    beschreibung:
      "Wer sich nicht traut, spricht nicht. Rituale und Lieder, die einem Kind das Gefühl geben, hier ist Platz für mich.",
    u3: ["Wer ist denn alles da", "Langsam wie ein Faultier", "Begrüßungsglocke", "Wir hören unseren Herzschlag", "Das kleine Eichhörnchen", "Viele Hände"],
    ue3: ["Eins und zwei und drei und vier", "Es regnet, es regnet, pitsch, pitsch, patsch", "Die vier Jahreszeiten", "Der Beschützer-Stein", "Die Gefühlssteine", "Das bin ich"],
  },
];

export function bausteinIcon(icon: number): string {
  return `${BILDER}/bausteine/${icon}.webp`;
}

export interface Material {
  /** Beschriftung des Etiketts, z. B. „2 Videos". */
  art: string;
  /** Orange statt Türkis (im Prototyp die `1` an zweiter Stelle). */
  betont: boolean;
  text: string;
}

export interface Lied {
  titel: string;
  herkunft: string;
  standbild: string;
  videoLabel: string;
  /**
   * Vimeo-Video der Vorschau, „ID" oder „ID/HASH" (nicht gelistete Videos).
   *
   * Noch leer: Die Kursvideos sind geschnitten, aber nicht auf Vimeo
   * (`15_AUFTRAG_Claude-Code_Vimeo-Upload.md`). Solange hier nichts steht,
   * zeigt die Seite nur das Standbild. Mit einer ID wird daraus beim Klick der
   * echte Player, sonst ändert sich nichts.
   */
  vimeo?: string;
  material: readonly Material[];
}

const m = (art: string, betont: 0 | 1, text: string): Material => ({ art, betont: betont === 1, text });

/** Array `M` aus dem Prototyp, 1:1. */
export const LIEDER: readonly Lied[] = [
  {
    titel: "Der Franz, der tanzt",
    herkunft: "Nicole Knorr · Baustein Bewegung und Sprache",
    standbild: `${BILDER}/lieder/franz.webp`,
    videoLabel: "Videovorschau · Lied und Umsetzung",
    // Vimeo-ID aus der Lektion „Der Franz, der tanzt" (Kurs musikalische-sprachfoerderung-ue3)
    vimeo: "1228281565",
    material: [
      m("2 Videos", 1, "Das Lied und die Umsetzung in der Gruppe"),
      m("Audio", 0, "Das Lied zum Mitsingen und Mitmachen"),
      m("Noten", 0, "Notenblatt als PDF zum Ausdrucken"),
      m("Bildkarten", 1, "Bildkarten zum Legen und Erzählen"),
      m("Spielidee", 1, "Schritt für Schritt, wie du es in der Gruppe machst"),
    ],
  },
  {
    titel: "Ich war's nicht",
    herkunft: "Nicole Knorr · Baustein Sprachbildung im Alltag",
    standbild: `${BILDER}/lieder/ichwarsnicht.webp`,
    videoLabel: "Videovorschau · mit den Ausreden-Monstern",
    // Vimeo-ID aus der Lektion „Ich war's nicht" (Kurs musikalische-sprachfoerderung-ue3)
    vimeo: "1227362805",
    material: [
      m("3 Videos", 1, "Lied, Spielidee und Umsetzung"),
      m("Audio", 0, "Zum Mitsingen"),
      m("Noten", 0, "Notenblatt als PDF"),
      m("Ausmalbild", 1, "Ausmalbild „Ausreden-Monster“ für die Kinder"),
      m("Spielidee", 1, "So bringst du es in den Alltag"),
    ],
  },
  {
    titel: "Bei uns in der Krippe",
    herkunft: "Kati Breuer · Baustein Sprachbildung im Alltag",
    standbild: `${BILDER}/lieder/krippe.webp`,
    videoLabel: "Videovorschau · Liedeinführung",
    // Vimeo-ID aus der Lektion „Bei uns in der Krippe" (Kurs musikalische-sprachfoerderung-u3)
    vimeo: "1227359798",
    material: [
      m("Video", 1, "Die Liedeinführung für die Krippe"),
      m("Audio", 0, "Das Lied als MP3"),
      m("Noten", 0, "Notenblatt als PDF"),
      m("Bildkarten", 1, "Bildkarten als PDF zum Ausdrucken"),
      m("Spielideen", 1, "Die Ideen für die Gruppe"),
    ],
  },
  {
    titel: "Obstsalat-Rap",
    herkunft: "Kati Breuer · Baustein Sprachbildung im Alltag",
    standbild: `${BILDER}/lieder/obstsalat.webp`,
    videoLabel: "Videovorschau · Liedeinführung in drei Teilen",
    // Vimeo-ID aus der Lektion „Obstsalat-Rap" (Kurs musikalische-sprachfoerderung-u3)
    vimeo: "1227359434",
    material: [
      m("3 Videos", 1, "Die Liedeinführung in drei Teilen"),
      m("Audio", 0, "Demoversion zum Mitrappen"),
      m("Noten", 0, "Notenblatt als PDF"),
      m("Spielideen", 1, "Die Ideen für die Gruppe"),
    ],
  },
];

/**
 * Player-Adresse für eine Vimeo-Angabe aus `Lied.vimeo`, oder `null`.
 *
 * Nimmt „123456789" und „123456789/abcdef0123" (nicht gelistet, mit Hash).
 * `autoplay=1` ist hier richtig: Der Player entsteht erst durch den Klick auf
 * das Standbild. Beim Laden der Seite spielt nichts und ist nichts zu hören.
 */
export function vimeoPlayerUrl(angabe: string | null | undefined): string | null {
  const treffer = /^\s*(\d{5,12})(?:\/([0-9a-f]{6,20}))?\s*$/i.exec(angabe ?? "");
  if (!treffer) return null;
  const [, id, hash] = treffer;
  const parameter = new URLSearchParams();
  if (hash) parameter.set("h", hash);
  parameter.set("autoplay", "1");
  parameter.set("dnt", "1");
  return `https://player.vimeo.com/video/${id}?${parameter.toString()}`;
}

/**
 * postMessage-Nachricht, mit der die Landingpage den Vimeo-Player nach der
 * Vorschau-Zeit anhält. Vimeo versteht JSON mit `method: "pause"`.
 */
export function vimeoPausenNachricht(): string {
  return JSON.stringify({ method: "pause" });
}

export interface Frage {
  frage: string;
  antwort: string;
}

/**
 * FAQ aus dem Prototyp. Seit dem Start am 15.10.2026 gibt es keinen Starttermin mehr: Einstieg jederzeit, der
 * Zugang ist mit der Buchung frei (Frage 296, Sitzung „Aufräumen“).
 */
export function faq(): readonly Frage[] {
  return [
    {
      frage: "Muss ich ein Instrument spielen können?",
      antwort:
        "Nein. Zu jedem Lied gibt es eine Audioaufnahme, die du einfach laufen lassen kannst. Noten liegen dabei, wenn du selbst begleiten möchtest.",
    },
    {
      frage: "Wie lange habe ich Zeit?",
      antwort:
        "So lange du willst. Es gibt kein Ablaufdatum und keine Frist. Als Orientierung: Der Umfang entspricht etwa 50 Unterrichtsstunden, die meisten sind in rund drei Monaten durch.",
    },
    {
      frage: "Ist die Ausbildung im Abo enthalten?",
      antwort:
        "Nein, sie wird separat gekauft. Als aktives Mitglied zahlst du aber den reduzierten Preis.",
    },
    {
      frage: "Wie läuft die Prüfung ab?",
      antwort:
        "Wenn du alle vier Kurse abgeschlossen hast, öffnet sich das Abschlussquiz mit 8 Fragen aus allen Kursen. Ab 6 richtigen Antworten hast du bestanden. Du kannst es so oft versuchen, wie du möchtest. Danach gibst du uns noch kurz Feedback und dein Zertifikat ist fertig.",
    },
    {
      frage: "Wann kann ich anfangen?",
      antwort:
        "Jederzeit. Mit deiner Anmeldung ist dein Zugang sofort frei und du bekommst eine E-Mail von uns. Die Live-Termine der Ausbildung siehst du in deinem Bereich.",
    },
  ];
}
